import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import CardsList from "@/components/CardsList";
import MatchCard from "@/components/CardsList/MatchCard";
import TrainingCard from "@/components/CardsList/TrainingCard";
import EventsCalendar from "@/components/Calendar/Events";

import {MatchAPI} from "@/api/match";
import {TrainingAPI} from "@/api/training";

import {ERROR_OPERATION} from "@/utils/constants";
import {fetchData} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

function EventsCalendarPage(props) {
	const {handleNotification, inPage} = props;

	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [matches, setMatches] = useState([]);
	const [trainings, setTrainings] = useState([]);
	const [currentView, setCurrentView] = useState("month");
	const [currentDate, setCurrentDate] = useState(new Date());
	const [filtersData, setFiltersData] = useState(
		JSON.parse(sessionStorage.getItem("events_calendar_filters")) || {},
	);

	let {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
	});
	categories = categories.filter((c) => c.label !== "Prima Squadra");

	const [loadedCategories, setLoadedCategories] = useState(false);
	const [lastCategoryId, setLastCategoryId] = useState();

	useEffect(() => {
		if (filtersData) {
			sessionStorage.setItem(
				"events_calendar_filters",
				JSON.stringify(filtersData),
			);
		}
	}, [filtersData]);

	const filters = [
		{
			key: "categoryId",
			type: "select",
			options: categories,
			sorted: false,
			label: "Categoria",
			customClassName: "col-12 col-sm-6 col-lg-4",
			clearable: false,
			value: filtersData?.categoryId,
			setValue: (value) => {
				setFiltersData({...filtersData, categoryId: value});
			},
		},
	];

	const getMatches = () => {
		fetchData(
			MatchAPI.list,
			{
				// categoryId: filtersData?.categoryId?.value,
				seasonId,
			},
			() => setLoading(true),
			(matches) =>
				setMatches(
					matches.map((m) => {
						return {...m, __eventType: "match"};
					}),
				),
			() => handleNotification(ERROR_OPERATION, "error"),
			() => setLoading(false),
		);
	};

	const getTrainings = () => {
		fetchData(
			TrainingAPI.list,
			{
				// categoryId: filtersData?.categoryId?.value,
				seasonId,
			},
			() => setLoading(true),
			(trainings) =>
				setTrainings(
					trainings.map((t) => {
						return {...t, __eventType: "training"};
					}),
				),
			() => handleNotification(ERROR_OPERATION, "error"),
			() => setLoading(false),
		);
	};

	useEffect(() => {
		if (categories?.length && !loadedCategories && !filtersData?.categoryId) {
			setFiltersData({
				...filtersData,
				categoryId: categories[0],
			});
			setLoadedCategories(true);
		}
	}, [categories]);

	useEffect(() => {
		if (
			filtersData?.categoryId &&
			filtersData?.categoryId.value !== lastCategoryId
		) {
			getMatches();
			getTrainings();
			setLastCategoryId(filtersData?.categoryId?.value);
		}
	}, [filtersData]);

	const data = useMemo(() => {
		return [...(matches || []), ...(trainings || [])].sort((a, b) => {
			const dateA = new Date(a.startDate || 0);
			const dateB = new Date(b.startDate || 0);
			return dateA - dateB;
		});
	}, [matches, trainings]);

	const listConfiguration = {
		data,
		onClick: (element) => {
			if (element.__eventType === "match") {
				navigate("/matches/" + element.id);
			} else {
				navigate("/trainings/" + element.id);
			}
		},
	};

	const renderItem = (element, key) => {
		if (element.__eventType === "match") {
			return (
				<MatchCard element={element} type="match_calendar" matchKey={key} />
			);
		}
		return <TrainingCard element={element} />;
	};

	const calendar = (
		<EventsCalendar
			events={data}
			view={currentView === "week" ? "week" : "month"}
			onSelectEvent={(e) => listConfiguration.onClick(e)}
			onNavigate={(d) => setCurrentDate(d)}
			onView={(v) => setCurrentView(v)}
			date={currentDate}
		/>
	);

	if (inPage) {
		return calendar;
	}

	return (
		<Page>
			<Loading visible={loading} />
			{calendar}
		</Page>
	);
}

export default EventsCalendarPage;
